import classNames from 'classnames';
import React from 'react';
import { HtmlDiv } from '../Controls/HtmlDiv';
import { HtmlHeading } from '../Controls/HtmlHeading';
import type { StrictOmit } from '../Utils/typeUtils';
import { assertNever } from '../Utils/typeUtils';
import type { HtmlString } from '~/Features/Types/HtmlString.csharp';
import type { BackgroundMediaSectionProps } from '~/Partials/BackgroundMediaSection/BackgroundMediaSection';
import { BackgroundMediaSection } from '~/Partials/BackgroundMediaSection/BackgroundMediaSection';
import { Button } from '~/Partials/Button/Button';
import { ButtonSize } from '~/Partials/Button/ButtonSize.csharp';
import { ButtonTier } from '~/Partials/Button/ButtonTier.csharp';
import { ButtonType } from '~/Partials/Button/ButtonType.csharp';
import type { LinkProps } from '~/Partials/Link/Link';
import { Link } from '~/Partials/Link/Link';
import { LinkType } from '~/Partials/Link/LinkType.csharp';
import { SvgIcon } from '~/Partials/SvgIcon/SvgIcon';
import styles from './ImageBackgroundCallToAction.module.css';
import type { ImageBackgroundCallToActionContext } from './ImageBackgroundCallToActionContext';
import type { ImageBackgroundCallToActionVariant } from './ImageBackgroundCallToActionVariant';
import { getHTMLHeading } from './Utils/ImageBackgroundCallToActionUtils';

export interface ImageBackgroundCallToActionButtonProps {
    type: 'button';
    label: string;
    onClick?: () => void;
}

export interface ImageBackgroundCallToActionLinkProps extends StrictOmit<LinkProps, 'type'> {
    type: 'link';
    label: string;
}

export interface ImageBackgroundCallToActionProps extends StrictOmit<BackgroundMediaSectionProps, 'layout'> {
    heading: HtmlString;
    callToAction: ImageBackgroundCallToActionLinkProps | ImageBackgroundCallToActionButtonProps;
    description?: HtmlString;
    context?: ImageBackgroundCallToActionContext;
    variant?: ImageBackgroundCallToActionVariant;
    className?: string;
}

export const ImageBackgroundCallToAction: React.FC<ImageBackgroundCallToActionProps> = props => {
    const {
        heading,
        description,
        callToAction,
        context = 'section',
        variant = 'default',
        overlayVariant = 'leftToRightDarkGreyGradient',
        className,
        ...restProps
    } = props;

    const renderCallToAction = () => {
        const renderContent = (label: string) => (
            <>
                <span>{label}</span>
                <SvgIcon iconName="arrow-right" />
            </>
        );

        const { type } = callToAction;
        switch (type) {
            case 'button':
                return (
                    <Button
                        tier={variant === 'prominent' ? ButtonTier.Tertiary : ButtonTier.Secondary}
                        type={ButtonType.Solid}
                        size={ButtonSize.Large}
                        onClick={callToAction.onClick}
                    >
                        {renderContent(callToAction.label)}
                    </Button>
                );

            case 'link': {
                return (
                    <Link
                        {...callToAction}
                        showIsExternal={false}
                        type={LinkType.Button}
                        buttonTier={ButtonTier.Secondary}
                    >
                        {renderContent(callToAction.label)}
                    </Link>
                );
            }

            default:
                assertNever(type);
        }
    };

    return (
        <BackgroundMediaSection
            className={classNames(styles.ImageBackgroundCallToAction, styles[`Variant--${variant}`], className)}
            overlayVariant={overlayVariant}
            layout={variant === 'prominent' ? 'grid' : variant}
            {...restProps}
        >
            <div className={styles.Content}>
                <div className={styles.Info}>
                    <HtmlHeading type={getHTMLHeading(context)} className={styles.Heading} html={heading} />
                    {description && <HtmlDiv className={styles.Description} html={description} />}
                </div>
                {renderCallToAction()}
            </div>
        </BackgroundMediaSection>
    );
};
